import React, { useState, useMemo } from 'react'
import { ExternalLink, TrendingUp, TrendingDown, Pencil, Trash2, ArrowUp, ArrowDown, Search } from 'lucide-react'
import { useThrottledTicks } from '../hooks/useThrottledTicks'

const fmt = (n, decimals = 2) =>
  n !== undefined && n !== null
    ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : '—'

// ── Sort header — defined OUTSIDE the component so React always sees the same
// component type and never unmounts/remounts on parent re-renders caused by
// WebSocket ticks.  Pass sort state down as props.
const SortHeader = ({ field, label, right = false, sortKey, sortDir, onSort }) => {
  const active = sortKey === field
  return (
    <th
      className={`py-3 px-3 font-medium text-gray-600 cursor-pointer select-none hover:bg-gray-100 transition-colors ${right ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-primary-500" /> : <ArrowDown className="w-3 h-3 text-primary-500" />)
          : <ArrowUp className="w-3 h-3 text-gray-300" />}
      </span>
    </th>
  )
}

/**
 * HoldingsTable
 *
 * Props:
 *  holdings[]   — array of normalised holding objects.
 *                 Each may carry _source ('live'|'manual'), _editable (bool), _manualIndex (number).
 *  isLive       — when true, show Buy/Sell for rows where _source !== 'manual'.
 *  brokerUrl    — URL to open for non-live Trade button.
 *  holdingAges  — map of symbol → { daysHeld, isLTCG } from tradebook FIFO. Optional.
 *  onBuyLive    — (holding) => void   called when Buy clicked
 *  onSellLive   — (holding) => void   called when Sell clicked
 *  onEdit       — (holding, index) => void   shown for editable rows
 *  onDelete     — (index) => void            shown for editable rows
 */
const HoldingsTable = ({
  holdings = [],
  isLive = false,
  brokerUrl,
  holdingAges = {},
  onBuyLive,
  onSellLive,
  onEdit,
  onDelete,
}) => {
  // Use throttled ticks (updates at most once per 5 s) so tick storms don't
  // cause rapid re-renders that make click events on SortHeader feel unresponsive.
  const symbolTicks = useThrottledTicks(5000)
  const [sortKey, setSortKey] = useState('symbol')
  const [sortDir, setSortDir] = useState('asc')
  const [search, setSearch]   = useState('')

  // Stable sort handler — passed down to the outer SortHeader component
  const handleSort = (field) => {
    if (sortKey === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(field); setSortDir('asc') }
  }

  // ── Build sortable rows (pre-compute live values for sort) ─────────────────
  // Wrapped in useMemo so the enrichment step only re-runs when holdings data
  // or the throttled tick snapshot actually changes (not on every parent render).
  const SORT_KEY_MAP = { ltp: '_liveLTP', currentValue: '_liveCurVal', pnl: '_livePnl', pnlPct: '_livePnlPct', qty: 'quantity', avgPrice: 'averagePrice' }

  const rows = useMemo(() => holdings.map((h, i) => {
    const streamTick = symbolTicks?.[h.symbol]
    const liveLTP    = streamTick?.ltp ?? h.ltp
    const qty        = (h.quantity || 0) + (h.t1Quantity || 0)
    const liveCurVal = qty > 0 ? liveLTP * qty : (h.currentValue ?? 0)
    const costBasis  = qty > 0 ? (h.averagePrice || 0) * qty : ((h.currentValue ?? 0) - (h.pnl ?? 0))
    const livePnl    = costBasis > 0 ? liveCurVal - costBasis : (h.pnl ?? 0)
    const livePnlPct = costBasis > 0 ? (livePnl / costBasis) * 100 : (h.pnlPercentage ?? 0)
    return { ...h, _i: i, _liveLTP: liveLTP, _liveCurVal: liveCurVal, _livePnl: livePnl, _livePnlPct: livePnlPct, _liveSource: streamTick ? 'stream' : 'api' }
  }), [holdings, symbolTicks]) // eslint-disable-line

  // ── Filter + sort ──────────────────────────────────────────────────────────
  const actualKey = SORT_KEY_MAP[sortKey] || sortKey
  const filtered = useMemo(() => [...rows]
    .filter(h => !search || h.symbol.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let va = a[actualKey] ?? 0, vb = b[actualKey] ?? 0
      if (typeof va === 'string') { va = va.toLowerCase(); vb = vb.toLowerCase() }
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1)
    }), [rows, search, sortKey, sortDir, actualKey]) // eslint-disable-line

  if (!holdings.length) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-sm">No holdings yet.</p>
        <p className="text-xs mt-1 text-gray-400">
          {isLive
            ? 'Live holdings will appear here once fetched from Kite.'
            : 'Use the + Add Holding button above to add one, or upload a JSON file below.'}
        </p>
      </div>
    )
  }

  return (
    <div>
      {/* Search bar */}
      <div className="flex items-center gap-2 mb-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Filter symbol…" className="input pl-8 py-1.5 text-sm w-40"
          />
        </div>
        {search && <span className="text-xs text-gray-400">{filtered.length}/{holdings.length} shown</span>}
      </div>
      <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50">
            <SortHeader field="symbol"       label="Instrument"  sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <SortHeader field="ltp"          label="LTP"         right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <SortHeader field="qty"          label="Qty"         right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <SortHeader field="avgPrice"     label="Avg Price"   right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <SortHeader field="currentValue" label="Cur Value"   right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <SortHeader field="pnl"          label="P&L"         right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <SortHeader field="pnlPct"       label="P&L %"       right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
            <th className="text-center py-3 px-3 font-medium text-gray-600">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {filtered.map((h) => {
            const i = h._i
            // Determine index for edit/delete calls
            const editIdx = h._manualIndex !== undefined ? h._manualIndex : i
            const isEditable    = h._editable !== false
            const showLiveTrade = isLive && h._source !== 'manual'
            const showBrokerLink = !isLive && brokerUrl
            const age = holdingAges?.[h.symbol]
            // Use pre-computed live values from sort step
            const liveLTP    = h._liveLTP
            const liveSource = h._liveSource
            const liveCurVal = h._liveCurVal
            const livePnl    = h._livePnl
            const livePnlPct = h._livePnlPct
            const pnlPositive = livePnl >= 0

            return (
              <tr key={`${h.symbol}-${i}`} className={`hover:bg-gray-50 transition-colors ${h._source === 'manual' ? 'bg-blue-50/30' : ''} ${liveSource === 'stream' ? 'border-l-2 border-l-success-200' : ''}`}>
                <td className="py-3 px-3">
                  <a
                    href={`https://www.tradingview.com/chart/?symbol=${h.exchange || 'NSE'}%3A${h.symbol}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 font-medium text-primary-600 hover:text-primary-700"
                  >
                    {h.symbol}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="text-xs text-gray-400">{h.exchange}</span>
                    {h._source === 'manual' && (
                      <span className="text-xs text-blue-400 font-medium">manual</span>
                    )}
                    {age && (
                      <span
                        title={`Oldest lot: ${age.daysHeld}d held (${age.isLTCG ? 'LTCG — >1yr, 12.5% tax' : 'STCG — <1yr, 20% tax'})`}
                        className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                          age.isLTCG
                            ? 'bg-success-100 text-success-700'
                            : 'bg-danger-100 text-danger-700'
                        }`}
                      >
                        {age.isLTCG ? 'LTCG' : 'STCG'}
                      </span>
                    )}
                  </div>
                </td>
                <td className="py-3 px-3 text-right font-medium">
                  <span className={liveSource === 'stream' ? 'text-success-700' : ''}>
                    ₹{fmt(liveLTP)}
                  </span>
                  {liveSource === 'stream' && (
                    <span className="ml-1 text-xs text-success-400" title="Live from AngelOne stream">●</span>
                  )}
                </td>
                <td className="py-3 px-3 text-right">
                  {h.quantity}
                  {h.t1Quantity > 0 && (
                    <span className="ml-1 text-xs text-warning-600">+{h.t1Quantity} T1</span>
                  )}
                </td>
                <td className="py-3 px-3 text-right">₹{fmt(h.averagePrice)}</td>
                <td className="py-3 px-3 text-right font-medium">₹{fmt(liveCurVal)}</td>
                <td className={`py-3 px-3 text-right font-medium ${pnlPositive ? 'text-success-600' : 'text-danger-600'}`}>
                  <div className="flex items-center justify-end gap-1">
                    {pnlPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {pnlPositive ? '+' : ''}₹{fmt(livePnl)}
                  </div>
                </td>
                <td className={`py-3 px-3 text-right font-medium ${pnlPositive ? 'text-success-600' : 'text-danger-600'}`}>
                  {pnlPositive ? '+' : ''}{fmt(livePnlPct)}%
                </td>
                <td className="py-3 px-3 text-center">
                  <div className="flex gap-1 justify-center items-center">
                    {/* Buy / Sell for live rows */}
                    {showLiveTrade && (
                      <>
                        <button
                          onClick={() => onBuyLive && onBuyLive(h)}
                          className="px-2 py-1 text-xs bg-success-100 text-success-700 hover:bg-success-200 rounded font-medium"
                        >
                          Buy
                        </button>
                        <button
                          onClick={() => onSellLive && onSellLive(h)}
                          className="px-2 py-1 text-xs bg-danger-100 text-danger-700 hover:bg-danger-200 rounded font-medium"
                        >
                          Sell
                        </button>
                      </>
                    )}

                    {/* Broker Trade link for non-live rows */}
                    {showBrokerLink && (
                      <a
                        href={brokerUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2 py-1 text-xs bg-primary-100 text-primary-700 hover:bg-primary-200 rounded font-medium flex items-center gap-1"
                      >
                        Trade <ExternalLink className="w-3 h-3" />
                      </a>
                    )}

                    {/* Edit button for editable rows */}
                    {isEditable && onEdit && (
                      <button
                        onClick={() => onEdit(h, editIdx)}
                        className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}

                    {/* Delete button for editable rows */}
                    {isEditable && onDelete && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Remove ${h.symbol} from this account?`)) {
                            onDelete(editIdx)
                          }
                        }}
                        className="p-1.5 text-gray-400 hover:text-danger-600 hover:bg-danger-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>   {/* overflow-x-auto */}
    </div>
  )
}

export default HoldingsTable
